import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { showLoadingOverlay, hideLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

// UI

import {
  Badge,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'

// Custom

import EditableTable from '@/components/Table/EditableTable'
import {
  hidePROnHoldConfirmationModal,
  PROnHoldConfirmationModalSelector,
} from '@/features/offers/slices/offerModal.slice'
import { createColumnHelper } from '@tanstack/react-table'
import { DateCell, PriceCell } from '@/components/Table/components/Cell'
import { toast } from 'react-toastify'
import useOfferPR from '@/features/offers/hooks/useOfferPR'
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'
import DownloadButton from '@/components/Elements/Button/DownloadButton'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { Currency } from '@eapproval/offer-shared'

const PROnHoldConfirmationModal = () => {
  const {
    show,
    newQuotations,
    offerId,
    costBudgetId,
    vendorIndex,
    costBudgetIndex,
    vendorSelectionIndex,
    submissionIndex,
  } = useSelector(PROnHoldConfirmationModalSelector)

  const [selectedIndex, setSelectedIndex] = useState(-1)
  const dispatch = useDispatch()

  const { useSelectNewQuotationForOnHold } = useOfferPR()

  const selectNewQuotationForOnHold = useSelectNewQuotationForOnHold()
  const closeModal = () => {
    setSelectedIndex(-1)
    dispatch(hidePROnHoldConfirmationModal())
  }

  const handleSelectNewQuotationForOnHold = async () => {
    //Find Select Quotation
    if (selectedIndex === -1) {
      toast.error('Please select 1 new quotation to proceed')
      return
    }

    if (selectedIndex > newQuotations.length) {
      toast.error('Unexpected Error: Invalid selected index')
      return
    }
    const selectedQuotation = newQuotations[selectedIndex]

    if (selectedQuotation?.historyIndex === undefined || selectedQuotation?.historyIndex < 0) {
      toast.error('Unexpected Error: Invalid selected quotation history index')
      return
    }

    try {
      dispatch(showLoadingOverlay())
      await selectNewQuotationForOnHold.mutate({
        offerId,
        costBudgetIndex,
        vendorSelectionIndex,
        submissionIndex,
        newQuotationIndex: selectedQuotation?.historyIndex,
      })
      closeModal()
    } catch (error) {
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const columnHelper = createColumnHelper()

  const handleOpenQuotationDetail = (quotation) => {
    if (!costBudgetId || vendorIndex < 0) {
      toast.error('Missing quotation detail reference')
      return
    }

    dispatch(
      showModal({
        modalType: MODAL_TYPES.OFFER_COST_BUDGET_QUOTATION_DETAIL,
        modalProps: {
          vendorHistoryIndex: quotation?.historyIndex,
          offerId,
          vendorIndex,
          costBudgetId,
          readOnly: true,
        },
      })
    )
  }

  const columnDef = useMemo(() => [
    columnHelper.display({
      id: 'selection',
      size: 10,
      cell: ({ row: { index } }) => {
        const selected = index === selectedIndex
        return (
          <Button onClick={() => setSelectedIndex(index)} disabled={selected} colorScheme={selected ? 'gray' : 'green'}>
            {selected ? 'Selected' : 'Select'}
          </Button>
        )
      },
    }),
    columnHelper.display({
      id: 'quotationType',
      header: 'Type',
      cell: ({ row: { original } }) => {
        if (original?.isOriginalQuotation) {
          return <Badge colorScheme="purple">Original</Badge>
        }

        return <Badge colorScheme="blue">New</Badge>
      },
    }),
    columnHelper.accessor('submissionDate', {
      header: 'Date',
      cell: (cell) => <DateCell format="DD-MMM-YYYY HH:mm:ss" {...cell} />,
    }),
    columnHelper.accessor('budget', {
      header: 'Amount ',
      cell: (cell) => <PriceCell {...cell} currency={cell.row.original?.currency ?? Currency.HKD} />,
    }),
    columnHelper.accessor('files', {
      header: 'Quotation File',
      cell: ({ getValue }) => {
        const files = getValue() ?? []

        if (!Array.isArray(files) || files.length === 0) {
          return <Text>-</Text>
        }

        return (
          <Flex direction="column" gap="2">
            {files.map((file) => (
              <Flex gap={2} alignItems="center" key={file?._id}>
                <Text mr="auto">{file?.originalFileName || ''}</Text>
                <ViewFileButton fileId={file?._id} />
                <DownloadButton fileId={file?._id} fileName={file?.originalFileName} />
              </Flex>
            ))}
          </Flex>
        )
      },
    }),
    columnHelper.display({
      id: 'quotationDetail',
      header: 'Quotation Detail',
      cell: ({ row: { original } }) => {
        if (original?.historyIndex === undefined || original?.historyIndex < 0) {
          return <Text>-</Text>
        }

        return (
          <Button colorScheme="blue" onClick={() => handleOpenQuotationDetail(original)}>
            Detail
          </Button>
        )
      },
    }),
  ])

  return (
    <Modal isOpen={show} onClose={closeModal} size="xl">
      <ModalOverlay />
      <ModalContent maxW="70%">
        <ModalHeader>Select New Quotation For PR Submission</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap="2">
            <EditableTable
              textAlign={'center'}
              columnDef={columnDef}
              data={newQuotations ?? []}
              tableWidth="100%"
              disabled={true}
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button colorScheme={'green'} onClick={handleSelectNewQuotationForOnHold}>
              Select Quotation
            </Button>
            <Button colorScheme="gray" onClick={closeModal}>
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PROnHoldConfirmationModal
